import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { MessagesModule } from 'primeng/messages';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DesignRevisionJudgementEditComponent } from '../../design-revision-judgement-edit';
import {
    createTreatmentDesignRevisionJudgementForm,
    TreatmentDesignRevisionJudgementForm,
    TreatmentDesignRevisionLiability,
} from '../../../core/model';
import { QCAcceptConfirmDialogConfig, QCAcceptConfirmDialogResult } from './model';

/**
 * A reusable dialog component for QC acceptance confirmation.
 * Displays the design revision judgement form for collecting liability and reason.
 * Opens via DynamicDialogService.
 */
@Component({
    selector: 'app-qc-accept-confirm-dialog',
    standalone: true,
    imports: [CommonModule, ButtonModule, MessagesModule, DesignRevisionJudgementEditComponent],
    templateUrl: './qc-accept-confirm-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QCAcceptConfirmDialogComponent implements OnInit {
    designRevisionJudgementForm?: TreatmentDesignRevisionJudgementForm | null;

    protected readonly ref = inject(DynamicDialogRef, { optional: true });
    protected readonly config: QCAcceptConfirmDialogConfig;

    private readonly _dialogConfig = inject<DynamicDialogConfig<QCAcceptConfirmDialogConfig>>(DynamicDialogConfig);

    constructor() {
        this.config = this._dialogConfig.data!;
    }

    ngOnInit(): void {
        this.initializeForm();
    }

    onConfirm(): void {
        const result: QCAcceptConfirmDialogResult = {
            confirmed: true,
            revisionJudgement: this.designRevisionJudgementForm?.getRawValue() ?? null,
        };
        this.ref?.close(result);
    }

    onCancel(): void {
        const result: QCAcceptConfirmDialogResult = {
            confirmed: false,
        };
        this.ref?.close(result);
    }

    private initializeForm(): void {
        const { config } = this;
        if (!config?.treatmentId) {
            return;
        }

        this.designRevisionJudgementForm = createTreatmentDesignRevisionJudgementForm({
            treatmentId: config.treatmentId,
        });

        if (config.designRevisionJudgement) {
            this.designRevisionJudgementForm.patchValue({
                ...config.designRevisionJudgement,
                liability: config.designRevisionJudgement.liability ?? TreatmentDesignRevisionLiability.pending,
            });
        }
    }
}
